import { useEffect, useState, useCallback } from "react";
import { 
  Search, 
  Download, 
  Eye, 
  EyeOff, 
  ShieldAlert, 
  Users, 
  ChevronLeft, 
  ChevronRight, 
  Loader2, 
  ArrowUpDown, 
  ArrowUp, 
  ArrowDown 
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const frosted = "border border-sky-900/30 bg-[#0a1224]/70 backdrop-blur-md shadow-[0_0_10px_rgba(56,189,248,0.05)]";

// Helper hook for debouncing search input
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

export default function CredentialsTable({ jobId, status }) {
  // DATA STATE
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // PAGINATION, SEARCH & SORT STATE
  const [page, setPage] = useState(1);
  const [limit] = useState(200); 
  const [search, setSearch] = useState("");
  const [meta, setMeta] = useState({ total: 0, totalPages: 0 });
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'desc' });
  
  // UI TOGGLES
  const [showPasswords, setShowPasswords] = useState(false);
  const debouncedSearch = useDebounce(search, 500);
const getJwtToken = () => localStorage.getItem("token") || "";

  // FETCH DATA FUNCTION
  const fetchData = useCallback(async () => {
    if (!jobId || status !== 'completed') return;
    
    setLoading(true);
    setError(null);
    
    try {
      const res = await fetch("https://whiteintel.io/api/regular/app/v2/investigations/get_job_creds.php", {
        method: "POST",
        headers: { "Content-Type": "application/json", 'Authorization': `Bearer ${getJwtToken()}`  },
        body: JSON.stringify({ 
            job_id: jobId, 
            page, 
            search: debouncedSearch,
            sort: sortConfig.key,
            order: sortConfig.direction
        }),
      });

      if (res.status === 401) {
        navigate('/signin');
        return; 
      }

      const json = await res.json();
      
      if (json.success) {
          setData(json.data);
          // Sync metadata from the background worker results
          if (json.meta) {
            setMeta({
                total: json.meta.total_records,
                totalPages: Math.ceil(json.meta.total_records / limit),
                page: json.meta.current_page
            });
          }
      } else {
          // If the worker is done but returned an error
          throw new Error(json.error || "Failed to load credentials.");
      }
    } catch (err) {
      console.error("Credentials Fetch Error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [jobId, status, page, debouncedSearch, sortConfig, limit, navigate]);

  // Load when Job status changes to 'completed' or when user interacts
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset page when search or sort changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, sortConfig]);

  // SORT HANDLER
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setPage(1);
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) return <ArrowUpDown className="w-3 h-3 opacity-30 group-hover:opacity-50" />;
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-3 h-3 text-sky-400" /> 
      : <ArrowDown className="w-3 h-3 text-sky-400" />;
  };

  const handleExport = () => {
    if (!data.length) return;
    const headers = ["App", "Username", "Password", "Computer", "IP", "Country"];
    const csvContent = [
      headers.join(","),
      ...data.map(row => 
        [row.app, row.username, row.password, row.computer_name, row.ip, row.country]
          .map(field => `"${String(field).replace(/"/g, '""')}"`)
          .join(",")
      )
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `credentials_job_${jobId}_page${page}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const val = (v) => (v && String(v).trim() !== "" ? v : <span className="text-neutral-600">-</span>);

  const columns = [
    { label: "Application", key: "app", width: "" },
    { label: "Username", key: "username", width: "" },
    { label: "Password", key: "password", width: "" },
    { label: "Computer", key: "computer_name", width: "" },
    { label: "IP Address", key: "ip", width: "w-32" },
    { label: "Country", key: "country", width: "w-24" }
  ];

  // 1. SHOW PROCESSING STATE
  if (status === 'pending' || status === 'processing') {
    return (
      <div className={`flex flex-col items-center justify-center h-[50vh] rounded-2xl ${frosted} border border-white/10`}>
         <div className="relative mb-6">
            <div className="absolute inset-0 bg-sky-500/20 blur-xl rounded-full animate-pulse" />
            <Loader2 className="w-10 h-10 text-sky-500 animate-spin relative z-10" />
         </div>
         <h3 className="text-sm font-bold text-white uppercase tracking-[0.2em]">Neural Link Active</h3>
         <p className="text-xs text-neutral-500 mt-2">Aggregating credential fragments for job {jobId.substring(0,8)}...</p>
      </div>
    );
  }

  // 2. SHOW FAILED STATE
  if (status === 'failed') {
    return (
      <div className={`flex flex-col items-center justify-center h-[50vh] rounded-2xl ${frosted} border border-red-500/20 text-center p-6`}>
          <ShieldAlert className="w-12 h-12 text-red-500 mb-4" />
          <h3 className="text-sm font-bold text-white uppercase">Correlation Failed</h3>
          <p className="text-xs text-neutral-500 mt-2 max-w-xs mx-auto">The background worker encountered an issue or timed out due to high data density.</p>
      </div>
    );
  }

  return (
    <div className={`flex flex-col h-full rounded-2xl ${frosted} overflow-hidden relative border border-white/10`}>
      
      {/* LOADING OVERLAY (For Pagination/Search) */}
      {loading && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-[#0a1224]/80 backdrop-blur-sm">
           <div className="flex flex-col items-center gap-3">
             <Loader2 className="w-8 h-8 text-sky-500 animate-spin" />
             <span className="text-sm font-medium text-white/40">Fetching deep records...</span>
           </div>
        </div>
      )}

      {/* TOOLBAR */}
      <div className="flex items-center justify-between p-4 border-b border-white/5 bg-neutral-900/50">
        <div className="flex items-center gap-4">
            <div className="relative group">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500 group-focus-within:text-sky-500 transition-colors" />
                <input 
                    type="text" 
                    placeholder="Search results..." 
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9 pr-4 py-2 bg-[#06101e] border border-neutral-800 rounded-lg text-sm text-white focus:outline-none focus:border-sky-500/50 focus:ring-1 focus:ring-sky-500/20 w-64 transition-all"
                />
            </div>
            
            <button
                onClick={() => setShowPasswords(!showPasswords)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium border transition-all ${
                    showPasswords 
                    ? "bg-amber-500/10 text-amber-400 border-amber-500/20 hover:bg-amber-500/20" 
                    : "bg-neutral-800/50 text-neutral-400 border-neutral-700 hover:bg-neutral-800 hover:text-white"
                }`}
            >
                {showPasswords ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                <span className="hidden sm:inline">{showPasswords ? "Hide" : "Show"} Passwords</span>
            </button>
        </div>
        
        <button 
          onClick={handleExport}
          disabled={loading || data.length === 0}
          className="flex items-center gap-2 px-3 py-2 bg-sky-600 hover:bg-sky-500 disabled:bg-neutral-800 disabled:text-neutral-500 text-white rounded-lg text-sm font-medium transition-colors shadow-lg shadow-sky-900/20"
        >
          <Download className="w-4 h-4" /> 
          <span className="hidden sm:inline">Export Page</span>
        </button>
      </div>

      {/* ERROR MESSAGE */}
      {error && !loading && (
          <div className="p-8 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-900/20 mb-3">
                  <ShieldAlert className="w-6 h-6 text-red-400" />
              </div>
              <p className="text-red-400 font-medium">Unable to load data</p>
              <p className="text-sm text-red-400/60 mt-1">{error}</p>
          </div>
      )}

      {/* TABLE */}
      <div className="flex-1 overflow-auto custom-scrollbar bg-[#06101e]">
        <table className="w-full text-left text-sm whitespace-nowrap">
          <thead className="bg-[#000414] sticky top-0 z-10 shadow-sm ring-1 ring-white/5">
            <tr>
              {columns.map((col, i) => (
                <th 
                  key={i} 
                  className={`px-4 py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wider border-b border-white/5 cursor-pointer hover:bg-white/5 hover:text-neutral-300 transition-colors select-none group ${col.width}`}
                  onClick={() => handleSort(col.key)}
                >
                  <div className="flex items-center gap-1.5">
                    {col.label}
                    {getSortIcon(col.key)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          
          <tbody className="divide-y divide-white/5">
            {data.length > 0 ? (
              data.map((row, i) => {
                 const rowBg = i % 2 === 0 ? "bg-transparent" : "bg-white/[0.02]";
                 return (
                  <tr key={i} className={`${rowBg} hover:bg-white/5 transition-colors group`}>
      
                    <td className="px-4 py-3 text-white font-medium">{val(row.app)}</td>
                    <td className="px-4 py-3 text-neutral-300">{val(row.username)}</td>
                    <td className="px-4 py-3 font-mono text-xs tracking-wider">
                      {row.password ? (
                          <span className={`${showPasswords ? "text-amber-400 bg-amber-400/10 px-1.5 py-0.5 rounded" : "text-neutral-600"}`}>
                              {showPasswords ? row.password : "••••••••••••"}
                          </span>
                      ) : (<span className="text-neutral-600">-</span>)}
                    </td>
                    <td className="px-4 py-3 text-neutral-400 text-xs">{val(row.computer_name)}</td>
                    <td className="px-4 py-3 text-sky-500/80 font-mono text-xs">{val(row.ip)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                          {row.country ? (
                              <>
                                <img src={`https://flagcdn.com/16x12/${row.country.toLowerCase()}.png`} alt={row.country} className="rounded-[1px] opacity-80" />
                                <span className="text-neutral-300">{row.country}</span>
                              </>
                          ) : (<span className="text-neutral-600">-</span>)}
                      </div>
                    </td>
                  </tr>
                 );
              })
            ) : (
              !loading && !error && (
                <tr>
                    <td colSpan="8" className="px-4 py-16 text-center text-neutral-500">
                        <div className="flex flex-col items-center justify-center opacity-50">
                            <div className="mb-2 uppercase tracking-widest text-[10px] font-bold">No records found</div>
                            <div className="text-[10px]">Neural dataset returned no direct matches for this pivot.</div>
                        </div>
                    </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>

      {/* PAGINATION FOOTER */}
      <div className="px-4 py-3 bg-[#000414] border-t border-white/5 text-xs text-neutral-400 flex justify-between items-center select-none">
        <div className="flex items-center gap-1">
            <span>Showing page <span className="text-white font-medium">{meta.page}</span> of <span className="text-white font-medium">{meta.totalPages || 1}</span></span>
            <span className="text-neutral-600 mx-2">|</span>
            <span>Total: <span className="text-white font-medium">{meta.total}</span> records</span>
        </div>

        <div className="flex items-center gap-2">
            <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1 || loading}
                className="p-1.5 rounded-md hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent transition-colors border border-white/10"
            >
                <ChevronLeft className="w-4 h-4" />
            </button>
            <button
                onClick={() => setPage(p => Math.min(meta.totalPages, p + 1))}
                disabled={page >= meta.totalPages || loading}
                className="p-1.5 rounded-md hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent transition-colors border border-white/10"
            >
                <ChevronRight className="w-4 h-4" />
            </button>
        </div>
      </div>
    </div>
  );
}